/**
 * SearchDrawer – manages the slide-in search panel.
 *
 * Opens when any [data-search-drawer-open] button is clicked.
 * Hides the "default state" (suggested / trending) while the user is typing
 * and restores it when the input is cleared.
 */
class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('#SearchDrawerPanel');
    this.overlay = this.querySelector('#SearchDrawerOverlay');
    this.closeBtn = this.querySelector('#SearchDrawerClose');
    this.input = this.querySelector('#SearchDrawer-Input');
    this.resetBtn = this.querySelector('.search-drawer__input-reset');
    this.defaultState = this.querySelector('#SearchDrawerDefault');
    this.predictiveEl = this.querySelector('predictive-search');
    this.form = this.querySelector('.search-drawer__form');
    this._queryCountCache = new Map();

    // Accessibility focus management
    this._lastFocusedElement = null;
  }

  connectedCallback() {
    // Close controls
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Input controls
    this.input?.addEventListener('input', () => this._onInput());
    this.form?.addEventListener('reset', () => {
      requestAnimationFrame(() => this._onInput());
    });

    // Keyboard handling
    this.addEventListener('keydown', (e) => this._onKeyDown(e));

    // Enable transitions after first paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-animated');
      });
    });

    // Wire up trigger buttons
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-search-drawer-open]');
      if (trigger) {
        this._lastFocusedElement = trigger; // Store trigger for focus return
        this.open();
      }
    });
  }

  open() {
    this.setAttribute('open', '');
    document.body.classList.add('search-drawer-open');

    // Focus the input after the transition starts
    requestAnimationFrame(() => {
      this.input?.focus();
    });
    this._onInput();
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('search-drawer-open');

    // Return focus to the trigger element
    if (this._lastFocusedElement) {
      this._lastFocusedElement.focus();
    }

    // Clear the input and reset the predictive search
    if (this.input) {
      this.input.value = '';
    }

    if (this.predictiveEl && typeof this.predictiveEl.close === 'function') {
      this.predictiveEl.close(true);
    } else {
      this.form?.dispatchEvent(new Event('reset'));
    }
    this._showDefault();
  }

  /**
   * Handles Escape key and Focus Trapping
   */
  _onKeyDown(event) {
    if (event.key === 'Escape') {
      this.close(); 
      return;
    }

    if (event.key === 'Tab') {
      this._handleFocusTrap(event);
    }
  }

  _handleFocusTrap(event) {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) { // Tab backwards
      if (document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      }
    } else { // Tab forwards
      if (document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }
  }

  _onInput() {
    const hasText = this.input?.value.trim().length > 0;
    if (hasText) {
      this._hideDefault();
      this._showReset();
    } else {
      this._showDefault();
      this._hideReset();
    }
  }

  _showDefault() {
    this.defaultState?.classList.remove('hidden');
  }

  _hideDefault() {
    this.defaultState?.classList.add('hidden');
  }

  _showReset() {
    this.resetBtn?.classList.remove('hidden');
  }

  _hideReset() {
    this.resetBtn?.classList.add('hidden');
  }
}

customElements.define('search-drawer', SearchDrawer);